import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, CreditCard, Download } from "lucide-react";
import WhatsAppIcon from "./WhatsAppIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const EVENT_META = {
  whatsapp: { label: "WhatsApp Click", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300", Icon: WhatsAppIcon },
  pay: { label: "Started $250 Checkout", cls: "border-[#E5C56F]/40 bg-[#E5C56F]/10 text-[#E5C56F]", Icon: CreditCard },
  signup: { label: "Downloaded PDF", cls: "border-purple-500/30 bg-purple-600/10 text-purple-300", Icon: Download },
};

const ActivityPanel = ({ adminKey }) => {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/analytics/intents`)
      .then(({ data }) => setItems(data.intents || []))
      .catch(() => setErr("Failed to load activity."));
  }, [adminKey]);

  if (err) return <div className="text-red-400 text-sm" data-testid="activity-error">{err}</div>;
  if (!items)
    return (
      <div className="flex items-center gap-3 text-zinc-400" data-testid="activity-loading">
        <Loader2 className="animate-spin" size={16} /> Loading activity…
      </div>
    );
  if (items.length === 0)
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-sm text-zinc-500" data-testid="activity-empty">
        No high-intent activity yet. WhatsApp clicks, $250 checkout starts and PDF downloads will appear here.
      </div>
    );

  return (
    <div className="space-y-3" data-testid="activity-panel">
      <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500">
        Last {items.length} high-intent actions — most recent first
      </div>
      {items.map((it, i) => {
        const meta = EVENT_META[it.event] || EVENT_META.whatsapp;
        const Icon = meta.Icon;
        const place = [it.city, it.country].filter(Boolean).join(", ") || "Location unknown";
        return (
          <div key={`${it.created_at}-${it.event}-${it.visitor_id || i}`} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 flex flex-wrap items-center gap-3" data-testid={`activity-item-${i}`}>
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] shrink-0 ${meta.cls}`}>
              <Icon size={12} /> {meta.label}
            </span>
            <div className="min-w-0 text-sm text-zinc-200">
              <span className="text-zinc-400">Visitor #{it.visitor_id}</span>{" "}
              from <span className="text-zinc-100">{place}</span>
              <span className="text-zinc-500"> · {it.device}</span>
              {it.visits > 1 ? (
                <span className="ml-2 inline-flex items-center rounded-full border border-[#E5C56F]/40 bg-[#E5C56F]/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] text-[#E5C56F]" data-testid={`activity-visits-${i}`}>
                  {it.visits}× visitor
                </span>
              ) : null}
              <div className="text-[11px] text-zinc-500 mt-0.5 truncate">
                {it.ip ? <>IP <span className="text-zinc-300 font-mono">{it.ip}</span> · </> : null}
                On page <span className="text-purple-300">{it.path}</span>
                {it.label ? <> · button: <span className="text-zinc-400">{it.label}</span></> : null}
                {" "}· viewed {it.pages_visited} page{it.pages_visited === 1 ? "" : "s"} this visit
              </div>
            </div>
            <div className="ml-auto text-[11px] text-zinc-500 shrink-0">{fmt(it.created_at)}</div>
          </div>
        );
      })}
    </div>
  );
};

export default ActivityPanel;
